import z from "zod";
import { cn } from "@/lib/utils";
import { useFormik } from "formik";
import { Button } from "../ui/button";
import Dialog from "../common/Dialog";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { toast } from "react-toastify";
import { CheckIcon } from "lucide-react";
import { useCallback, useState } from "react";
import Link from "../../assets/svg/Link.svg";
import PlusSvg from "../../assets/svg/Plus.svg";
import ErrorMessage from "../common/ErrorMessage";
import Select, { SingleValue } from "react-select";
import TrashCan from "../../assets/svg/TrashCan.svg";
import DownArrow from "../../assets/svg/DownArrow.svg";
import useCreateTaskMutation, {
  Task,
} from "@/api/mutation/useTaskCreateMutation";
import { toFormikValidationSchema } from "zod-formik-adapter";
import { TaskDependenciesEnumValue } from "@backend/src/schemas/enums";
import { dependenciesTaskSchema } from "@backend/src/schemas/taskSchema";
import useTaskDependenciesMutation from "@/api/mutation/useTaskDependenciesmutaion";
import useRemoveTaskDependenciesMutation from "@/api/mutation/useTaskRemoveDependencies";
import InputText from "../common/InputText";
import { errorConfig } from "@/lib/toastError";
import { generateUniqueTaskName } from "../shared/taskNameGenrater";
import useProjectQuery from "@/api/query/useProjectQuery";
import { useTranslation } from "react-i18next";
import { errorKeyFormate } from "@/helperFuntions/errroMessage";

type Props = {
  task?: Task;
  projectId?: string;
  endTask?: string;
  refetch: () => void;
  allowed?: boolean;
  setTaskId: (id: string) => void;
  setTask: (task: Task) => void;
  allTask:Task[]
};

type Options = { value: string; label: string };

const reactSelectStyle = {
  control: (
    provided: Record<string, unknown>,
    state: { isFocused: boolean }
  ) => ({
    ...provided,
    border: "1px solid #E7E7E7",
    outline: state.isFocused ? "2px solid #299d91" : "1px solid #E7E7E7",
    boxShadow: state.isFocused ? "0px 0px 0px #299d91" : "none",
    "&:hover": {
      outline: state.isFocused ? "2px solid #299d91" : "1px solid #E7E7E7",
      boxShadow: "0px 0px 0px #299d91",
    },
  }),
  option: (
    provided: Record<string, unknown>,
    state: { isSelected: boolean }
  ) => ({
    ...provided,
    color: state.isSelected ? "white" : "black",
    background: state.isSelected ? "#299d91" : "white",
    "&:hover": {
      color: "white",
      background: "#299d9199",
    },
  }),
};

function TaskDependencies(props: Props) {
  const {allTask} = props;
  const { t } = useTranslation();
  const [dependenciesShow, setDependenciesShow] = useState<boolean>(
    Boolean(props.endTask) ?? false
  );
  const [showConfirmDelete, setShowConfirmDelete] = useState("");
  const taskDependenciesMutation = useTaskDependenciesMutation(
    props.task?.taskId
  );
  const removeTaskDependenciesMutation = useRemoveTaskDependenciesMutation();
  const val = allTask.find((d) => d.taskId == props.endTask);

  const [defaultsValue, setDefaultsValue] = useState<Options>(
    val ? { label: val?.taskName, value: val?.taskId } : ({} as Options)
  );
  
  const taskCreateMutation = useCreateTaskMutation(
    props.projectId,
    props.task?.taskId ?? ""
  );
  const dependencies = useCallback((): Options[] => {
    if (!allTask) return [];
  
    const allTasks = allTask;
  
    // Helper function to get all ancestors of a task
    const getAncestors = (taskId: string, tasks: Task[]): string[] => {
      const task = tasks.find((t) => t.taskId === taskId);
      if (!task || !task.parentTaskId) return [];
      return [task.parentTaskId, ...getAncestors(task.parentTaskId, tasks)];
    };
  
    // Helper function to get all descendants of a task
    const getDescendants = (taskId: string, tasks: Task[]): string[] => {
      const subtasks = tasks.filter((t) => t.parentTaskId === taskId);
      return subtasks.length > 0
        ? subtasks.flatMap((sub) => [sub.taskId, ...getDescendants(sub.taskId, tasks)])
        : [];
    };
  
    // Get all ancestors and descendants of the current task
    const taskAncestors = props.task?.taskId ? getAncestors(props.task.taskId, allTasks) : [];
    const taskDescendants = props.task?.taskId ? getDescendants(props.task.taskId, allTasks) : [];
  
    return allTasks
      .filter((t) => {
        if (t.taskId === props.task?.taskId) return false; // A task cannot depend on itself
        if (taskAncestors.includes(t.taskId)) return false; // Prevent dependency on ancestors
        if (taskDescendants.includes(t.taskId)) return false; // Prevent dependency on descendants
        return true;
      })
      .map((t) => ({ label: t.taskName, value: t.taskId }));
  }, [allTask, props.task]); // Dependencies: re-run when `allTask.data` or `props.task` changes
  

  const handleDependencies = (val: SingleValue<Options>) => {
    if (val) {
      setDefaultsValue({ label: val.label, value: val.value });
      dependenciesFormik.setFieldValue(
        "dependendentOnTaskId",
        val.value ?? null
      );
    }
  };

  const dependenciesFormik = useFormik<z.infer<typeof dependenciesTaskSchema>>({
    initialValues: {
      dependentType: TaskDependenciesEnumValue.PREDECESSORS,
      dependendentOnTaskId: props.endTask ? defaultsValue.value : "",
    },
    validationSchema: toFormikValidationSchema(dependenciesTaskSchema),
    onSubmit: async (values) => {
      if (!props.task?.taskId) {
        await createTaskWithValue();
      }
      taskDependenciesMutation.mutate(
        { ...values, taskId: tempId ? tempId : props.task?.taskId },
        {
          onSuccess(data) {
            props.refetch();
            setDefaultsValue({ label: "select", value: "" });
            setDependenciesShow(false);
            toast.success(data.data.message);
            dependenciesFormik.resetForm();
          },
          onError(error) {
            toast.error(
              error.response?.data.message == "Unauthorized"
                ? t(
                  "You are not authorized to edit tasks which are not assigned to you"
                )
                : errorKeyFormate(error.response?.data.message + ""),
              errorConfig
            );
          },
        }
      );
    },
  });

  const removeDependency = () => {
    removeTaskDependenciesMutation.mutate(showConfirmDelete, {
      onSuccess(data) {
        props.refetch();
        toast.success(data.data.message);
        setShowConfirmDelete("");
      },
      onError(error) {
        toast.error(
          error.response?.data.message == "Unauthorized"
            ? t(
              "You are not authorized to edit tasks which are not assigned to you"
            )
            : errorKeyFormate(error.response?.data.message + "")
        );
        setShowConfirmDelete("");
      },
    });
  };
  const projects = useProjectQuery();
  let tempId = "";

  const createTaskWithValue = (value?: Task) => {
    const project = projects?.data?.data?.data.find(
      (p) => p.projectId === props.projectId
    );
    const projectStartDate = project?.startDate
      ? new Date(project.startDate)
      : new Date();

    const data = {
      taskName: generateUniqueTaskName(
        allTask ?? [],
        project?.projectName ?? ""
      ),
      taskDescription: "",
      startDate: value?.startDate
        ? new Date(value.startDate)
        : projectStartDate >= new Date()
          ? projectStartDate
          : new Date(),
      duration: 1.0,
    };

    return new Promise<void>((resolve, reject) => {
      taskCreateMutation.mutate(data, {
        onSuccess(res) {
          props.setTaskId(res.data.data.taskId);
          props.setTask(res.data.data);
          tempId = res.data.data.taskId;
          resolve();
        },
        onError(error) {
          toast.error( errorKeyFormate(error.response?.data.message+''), errorConfig);
          reject(error);
        },
      });
    });
  };

  return (
    <div className="w-full">
      <div className="flex items-center gap-2.5 mt-4 w-full md:w-[49%]">
        <div className="flex items-center justify-between gap-2.5 border px-1.5 py-2 h-8 rounded-md w-full">
          <div className="text-sm font-medium text-nowrap w-full flex items-center gap-1">
            <img src={Link} className="w-4 h-4 mr-1" />
            {t("dependencies")}
          </div>
          {!dependenciesShow && props.allowed && (
            <Button
              variant={"none"}
              className=" p-0"
              onClick={() => {
                setDependenciesShow(true);
              }}
            >
              <img src={PlusSvg} className="w-4 h-4 brightness-0" />
            </Button>
          )}
        </div>
      </div>
      {Boolean(
        props.task?.dependencies && props.task?.dependencies.length > 0
      ) &&
        props.task?.dependencies.map((dependency) => {
          return (
            <div className="flex gap-2 items-center mt-3 ">
              <div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild disabled>
                    <div className="py-2 px-4 rounded-md text-sm font-medium bg-slate-100 hover:bg-slate-100/80 w-44 h-10">
                      <div className="flex items-center justify-between gap-4 w-full">
                        <div>{t(dependency.dependentType)}</div>
                        <div className="w-8 flex justify-end">
                          <img src={DownArrow}></img>
                        </div>
                      </div>
                    </div>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-44 bg-white shadow-md rounded-md">
                    {Object.keys(TaskDependenciesEnumValue).map(
                      (item: string, index) => {
                        return (
                          <DropdownMenuItem
                            className={`p-2 ${
                              dependency.dependentType == item
                                ? "bg-gray-100"
                                : ""
                            }`}
                            key={index}
                          >
                            <div
                              className={`flex justify-between text-sm font-medium text-gray-500 relative cursor-pointer p-1 w-full`}
                            >
                              <div className="flex items-center justify-between w-full gap-2">
                                <div className="text-sm font-medium text-gray-500 relative cursor-pointer">
                                  {t(item)}
                                </div>
                                <CheckIcon
                                  className={cn(
                                    "ml-auto h-4 w-4",
                                    item === dependency.dependentType
                                      ? "opacity-100"
                                      : "opacity-0"
                                  )}
                                />
                              </div>
                            </div>
                          </DropdownMenuItem>
                        );
                      }
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <div className="w-full">
                {dependency.dependentOnTask?.taskId && (
                  <InputText
                    className="py-0 h-10 mt-0"
                    value={dependency.dependentOnTask?.taskName}
                    disabled
                  />
                )}
              </div>
              {props.allowed && <div>
                <Button
                  variant={"none"}
                  className="p-0 h-6 w-6"
                  onClick={() =>
                    setShowConfirmDelete(dependency.taskDependenciesId)
                  }
                >
                  <img src={TrashCan} />
                </Button>
              </div>}
            </div>
          );
        })}
      {dependenciesShow && (
        <>
          <div className="flex gap-2 items-start mt-3 ">
            <div className="relative">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <div className="py-2 px-4 rounded-md text-sm font-medium bg-slate-100 hover:bg-slate-100/80 w-44 h-10">
                    <div className="flex items-center justify-between gap-4 w-full">
                      <div>{t(dependenciesFormik.values.dependentType)}</div>
                      <div className="w-8 flex justify-end">
                        <img src={DownArrow}></img>
                      </div>
                    </div>
                  </div>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-44 bg-white shadow-md rounded-md"  onBlur={()=>dependenciesFormik.setFieldTouched("dependentType")}> 
                  {Object.keys(TaskDependenciesEnumValue).map(
                    (item: string, index) => {
                      return (
                        <DropdownMenuItem
                          className={`p-2 ${
                            dependenciesFormik.values.dependentType == item
                              ? "bg-gray-100"
                              : ""
                          }`}
                          key={index}
                          onClick={() => {
                            dependenciesFormik.setFieldValue(
                              "dependentType",
                              TaskDependenciesEnumValue[
                                item as keyof typeof TaskDependenciesEnumValue
                              ]
                            );
                          }}
                        >
                          <div
                            className={`flex justify-between text-sm font-medium text-gray-500 relative cursor-pointer p-1 w-full`}
                          >
                            <div className="flex items-center justify-between w-full gap-2">
                              <div className="text-sm font-medium text-gray-500 relative cursor-pointer">
                                {t(item)}
                              </div>
                              <CheckIcon
                                className={cn(
                                  "ml-auto h-4 w-4",
                                  item ===
                                    dependenciesFormik.values.dependentType
                                    ? "opacity-100"
                                    : "opacity-0"
                                )}
                              />
                            </div>
                          </div>
                        </DropdownMenuItem>
                      );
                    }
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <div className="w-full">
              <Select
                placeholder={t("select_tasks")}
                styles={reactSelectStyle}
                defaultValue={defaultsValue}
                options={dependencies()}
                onChange={handleDependencies}
                onBlur={(e)=>dependenciesFormik.handleBlur(e)}
                isDisabled={Boolean(props.endTask)}
                menuPlacement="auto"
              />
            </div>
          </div>
          <ErrorMessage>
            {dependenciesFormik.errors.dependentType && dependenciesFormik.touched.dependentType && 
              dependenciesFormik.errors.dependentType}
          </ErrorMessage>
          <ErrorMessage>
            {dependenciesFormik.errors.dependendentOnTaskId && dependenciesFormik.touched.dependendentOnTaskId &&  dependenciesFormik.errors.dependendentOnTaskId}
          </ErrorMessage>
          <div className="flex justify-end gap-2">
            <div>
              <Button
                variant={"secondary"}
                onClick={() => {
                  dependenciesFormik.resetForm(), setDependenciesShow(false);
                }}
              >
                {t("cancel")}
              </Button>
            </div>
            <div>
              <Button
                variant={"primary_outline"}
                onClick={dependenciesFormik.submitForm}
              >
                {t("create")}
              </Button>
            </div>
          </div>
        </>
      )}
      <Dialog
        isOpen={showConfirmDelete ? true : false}
        onClose={() => {}}
        modalClass="rounded-lg"
      >
        <div className="flex flex-col gap-2 p-6 ">
          <img src={TrashCan} className="w-12 m-auto" />{" "}
          {t("common_delete_message")}
          <div className="flex gap-2 ml-auto">
            <Button
              variant={"outline"}
              isLoading={removeTaskDependenciesMutation.isPending}
              disabled={removeTaskDependenciesMutation.isPending}
              onClick={() => setShowConfirmDelete("")}
            >
              {t("cancel")}
            </Button>
            <Button variant={"primary"} onClick={removeDependency}>
              {t("delete")}
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}

export default TaskDependencies;
